import { useEffect, useState } from 'react';
import { api, type DevAccount } from '../client/api';
import { cn } from '../utils/cn';

// =============================================================================
// 개발용 계정 전환 — pnpm dev:roles 에서만 보인다(서버가 /api/auth/dev-accounts 를 열 때).
// 역할별 화면 차이를 클릭 한 번으로 비교하기 위한 도구. 운영 빌드에서는 서버가 404를 돌려
// 아무것도 렌더하지 않는다.
// =============================================================================

export function useDevAccounts(): DevAccount[] | null {
  const [accounts, setAccounts] = useState<DevAccount[] | null>(null);
  useEffect(() => {
    if (!import.meta.env.DEV) return;
    let alive = true;
    // 동기 예외(모듈 mock 등)도 거부로 바꿔 화면을 깨뜨리지 않는다 — 이 패널은 있으면 좋고 없어도 되는 도구.
    void Promise.resolve().then(() => api.auth.devAccounts()).then((list) => { if (alive) setAccounts(list && list.length ? list : null); }).catch(() => { if (alive) setAccounts(null); });
    return () => { alive = false; };
  }, []);
  return accounts;
}

async function switchTo(email: string, onDone: () => void): Promise<void> {
  await api.auth.devLogin(email);
  onDone();
}

/** 로그인 화면용 — 계정 목록을 버튼으로. */
export function DevAccountPanel({ onAuthenticated }: { onAuthenticated: () => void }): JSX.Element | null {
  const accounts = useDevAccounts();
  const [pending, setPending] = useState<string | null>(null);
  if (!accounts) return null;
  return (
    <section aria-label="개발용 계정 전환" className="mt-8 rounded-md border border-dashed border-line p-4">
      <p className="mb-3 text-[12px] font-semibold text-text-dim">개발용 계정 전환 · 로컬 전용</p>
      <ul className="flex flex-col gap-1.5">
        {accounts.map((account) => (
          <li key={account.email}>
            <button
              type="button"
              disabled={pending !== null}
              onClick={() => { setPending(account.email); void switchTo(account.email, onAuthenticated).catch(() => setPending(null)); }}
              className={cn('flex w-full items-center justify-between gap-3 rounded border border-line px-3 py-2 text-left text-[13px] hover:border-accent hover:bg-accent-soft disabled:opacity-50')}
            >
              <span className="font-medium">{account.name ?? account.email}</span>
              <span className="truncate text-text-dim">{account.role} · {account.scope}</span>
            </button>
          </li>
        ))}
      </ul>
    </section>
  );
}

/** 상단바용 — 로그인 상태에서 다른 역할로 바로 갈아탄다(새로고침). */
export function DevAccountSelect({ currentEmail }: { currentEmail: string | undefined }): JSX.Element | null {
  const accounts = useDevAccounts();
  if (!accounts) return null;
  return (
    <label className="flex items-center gap-1.5 text-[12px] text-text-dim" title="개발용 계정 전환 (로컬 전용)">
      <span className="rounded bg-warning/20 px-1.5 py-0.5 font-semibold text-warning">DEV</span>
      <select
        aria-label="개발용 계정 전환"
        className="h-7 rounded border border-line bg-panel-2 px-1.5 text-[12px] text-text"
        value={currentEmail ?? ''}
        onChange={(event) => { const email = event.target.value; if (email && email !== currentEmail) void switchTo(email, () => window.location.reload()); }}
      >
        {!accounts.some((a) => a.email === currentEmail) && <option value="">현재 계정</option>}
        {accounts.map((account) => <option key={account.email} value={account.email}>{account.name ?? account.email} · {account.role}</option>)}
      </select>
    </label>
  );
}
